import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { PostAddressListParseAPI } from "@/features/dmManagement/api/postAddressListParseAPI";
import { GetOrderAmountAPI } from "@/features/dmManagement/api/getOrderAmountAPI";
import { PostOrderAPI } from "@/features/dmManagement/api/postOrderAPI";
import { useApiClient } from "@/hooks/useApiClient";
import type {
  OrderFormData,
  ParseResult,
  ApiCallStatus,
} from "@/features/dmManagement/types";
import { validateAddressListFile } from "@/features/dmManagement/utils/fileValidation";
import { extractFileError } from "@/features/dmManagement/utils/extractFileError";

const orderFormSchema = z.object({
  taskId: z.preprocess(
    (val) => (val === "" || val === undefined ? undefined : Number(val)),
    z
      .number({
        required_error: "識別番号を入力してください",
        invalid_type_error: "識別番号を入力してください",
      })
      .int("整数を入力してください")
      .positive("正の整数を入力してください")
  ),
  taskName: z
    .string({ required_error: "識別名を入力してください" })
    .min(1, "識別名を入力してください")
    .max(256, "256文字以内で入力してください"),
  dmDesignId: z.number({ required_error: "デザインを選択してください" }),
  addressListFile: z.instanceof(File, {
    message: "ファイルを選択してください",
  }),
  replacementComponents: z
    .array(
      z.object({
        designComponentId: z.number(),
        file: z.instanceof(File),
      })
    )
    .optional(),
});

interface UseOrderFormReturn {
  form: ReturnType<typeof useForm<OrderFormData>>;
  parseResult: ParseResult | null;
  parseStatus: ApiCallStatus;
  parseError: string | null;
  amount: number | null;
  unitPrice: number | null;
  amountStatus: ApiCallStatus;
  isSubmitting: boolean;
  canSubmit: boolean;
  handleFileSelect: (file: File) => void;
  handleFileRejected: () => void;
  handleDesignChange: (designId: number) => void;
  handleSubmit: () => Promise<void>;
}

/**
 * 注文フォームの状態管理フック。
 *
 * 以下の非同期フローを管理する:
 * 1. ファイル選択 → executeParse（宛先リスト解析）
 * 2. パース成功 → executeAmount（デザイン×件数で料金計算）
 * 3. デザイン変更 → executeAmount（料金再計算）
 *
 * AbortController で前回のリクエストをキャンセルし、競合を防止する。
 */
export const useOrderForm = (): UseOrderFormReturn => {
  const { apiClient } = useApiClient();
  const navigate = useNavigate();

  const form = useForm<OrderFormData>({
    mode: "all",
    resolver: zodResolver(orderFormSchema),
  });

  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseStatus, setParseStatus] = useState<ApiCallStatus>("idle");
  const [parseError, setParseError] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  const [amountStatus, setAmountStatus] = useState<ApiCallStatus>("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parseAbortRef = useRef<AbortController | null>(null);
  const amountAbortRef = useRef<AbortController | null>(null);
  const currentFileRef = useRef<File | null>(null);
  const currentDesignIdRef = useRef<number | null>(null);

  // フォーム入力OK + パース成功（エラーなし） + 料金計算完了 + 送信中でない で送信可能
  const canSubmit = useMemo(
    () =>
      form.formState.isValid &&
      parseStatus === "success" &&
      parseResult?.hasError === false &&
      amountStatus === "success" &&
      !isSubmitting,
    [
      form.formState.isValid,
      parseStatus,
      parseResult?.hasError,
      amountStatus,
      isSubmitting,
    ]
  );

  const executeAmount = useCallback(
    async (designId: number, totalRows: number): Promise<void> => {
      amountAbortRef.current?.abort();

      const controller = new AbortController();
      amountAbortRef.current = controller;

      setAmountStatus("loading");

      try {
        const result = await GetOrderAmountAPI(
          apiClient,
          { dmDesignId: designId, quantity: totalRows },
          controller.signal
        );

        if (controller.signal.aborted) return;

        setAmount(result.amount);
        setUnitPrice(result.unitPrice);
        setAmountStatus("success");
      } catch {
        if (controller.signal.aborted) return;
        setAmount(null);
        setUnitPrice(null);
        setAmountStatus("error");
        toast.error("料金計算に失敗しました");
      }
    },
    [apiClient]
  );

  const executeParse = useCallback(
    async (file: File): Promise<void> => {
      parseAbortRef.current?.abort();
      amountAbortRef.current?.abort();

      const controller = new AbortController();
      parseAbortRef.current = controller;

      setParseResult(null);
      setParseError(null);
      setParseStatus("loading");
      setAmount(null);
      setUnitPrice(null);
      setAmountStatus("idle");

      try {
        const result = await PostAddressListParseAPI(
          apiClient,
          file,
          controller.signal
        );

        if (controller.signal.aborted) return;

        setParseResult(result);
        setParseStatus("success");

        const designId = currentDesignIdRef.current;
        if (designId && !result.hasError) {
          void executeAmount(designId, result.totalRows);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        setParseStatus("error");

        const message =
          extractFileError(error) ?? "宛先リストのパースに失敗しました";
        setParseError(message);
        toast.error(message);
      }
    },
    [apiClient, executeAmount]
  );

  const handleFileSelect = useCallback(
    (file: File): void => {
      const validationError = validateAddressListFile(file);
      if (validationError) {
        setParseError(validationError);
        setParseStatus("error");
        setParseResult(null);
        setAmount(null);
        setUnitPrice(null);
        setAmountStatus("idle");
        return;
      }

      currentFileRef.current = file;
      form.setValue("addressListFile", file, { shouldValidate: true });
      setParseError(null);

      void executeParse(file);
    },
    [form, executeParse]
  );

  const handleFileRejected = useCallback((): void => {
    parseAbortRef.current?.abort();
    amountAbortRef.current?.abort();
    currentFileRef.current = null;
    form.setValue("addressListFile", undefined as never, {
      shouldValidate: true,
    });
    setParseResult(null);
    setParseError(null);
    setParseStatus("idle");
    setAmount(null);
    setUnitPrice(null);
    setAmountStatus("idle");
  }, [form]);

  const handleDesignChange = useCallback(
    (designId: number): void => {
      currentDesignIdRef.current = designId;
      form.setValue("dmDesignId", designId, { shouldValidate: true });

      amountAbortRef.current?.abort();
      setAmount(null);
      setUnitPrice(null);
      setAmountStatus("idle");

      if (parseResult && !parseResult.hasError) {
        void executeAmount(designId, parseResult.totalRows);
      }
    },
    [form, parseResult, executeAmount]
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const formData = form.getValues();
      await PostOrderAPI(apiClient, formData);
      toast.success("注文が完了しました");
      navigate("/dmManagement/orders");
    } catch {
      toast.error("注文に失敗しました");
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, form, apiClient, navigate]);

  useEffect(() => {
    return () => {
      parseAbortRef.current?.abort();
      amountAbortRef.current?.abort();
    };
  }, []);

  return {
    form,
    parseResult,
    parseStatus,
    parseError,
    amount,
    unitPrice,
    amountStatus,
    isSubmitting,
    canSubmit,
    handleFileSelect,
    handleFileRejected,
    handleDesignChange,
    handleSubmit,
  };
};
